import { Route } from '@angular/router';

import { AccountGuard } from './account/providers/account.guard';
import { SignInGuard } from './account/providers/sign-in.guard';
import { CheckoutResolver } from './checkout/providers/checkout-resolver';
import { CheckoutGuard } from './checkout/providers/checkout.guard';
import { HomePageComponent } from './core/components/home-page/home-page.component';
import { AppRoutes } from './core/enums/routes.enum';
import { ProductResolver } from './core/seo/product.resolver';
import { ContactsInfo } from './custom/common';

export const routes: Route[] = [
    {
        path: '',
        component: HomePageComponent,
        pathMatch: 'full',
    },
    {
        path: 'category/:slug',
        loadComponent: () =>
            import('./core/components/product-list/product-list.component').then(
                m => m.ProductListComponent,
            ),
        pathMatch: 'full',
    },
    {
        path: 'search',
        loadComponent: () =>
            import('./core/components/product-list/product-list.component').then(
                m => m.ProductListComponent,
            ),
    },
    {
        path: `${AppRoutes.Product}/:slug`,
        loadComponent: () =>
            import('./core/components/product-detail/product-detail.component').then(
                m => m.ProductDetailComponent,
            ),
        resolve: {
            product: ProductResolver,
        },
    },
    {
        path: 'account',
        loadComponent: () =>
            import('./account/components/account/account.component').then(
                m => m.AccountComponent,
            ),
        children: [
            {
                path: '',
                pathMatch: 'full',
                canActivate: [AccountGuard],
                loadComponent: () =>
                    import('./account/components/account-dashboard/account-dashboard.component').then(
                        m => m.AccountDashboardComponent,
                    ),
            },
            {
                path: 'orders',
                canActivate: [AccountGuard],
                loadComponent: () =>
                    import('./account/components/account-order-list/account-order-list.component').then(
                        m => m.AccountOrderListComponent,
                    ),
            },
            {
                path: 'orders/:code',
                canActivate: [AccountGuard],
                loadComponent: () =>
                    import('./account/components/account-order-detail/account-order-detail.component').then(
                        m => m.AccountOrderDetailComponent,
                    ),
            },
            {
                path: 'address-book',
                canActivate: [AccountGuard],
                loadComponent: () =>
                    import('./account/components/account-address-book/account-address-book.component').then(
                        m => m.AccountAddressBookComponent,
                    ),
            },
            {
                path: 'address-book/:id',
                canActivate: [AccountGuard],
                loadComponent: () =>
                    import('./account/components/account-address-detail/account-address-detail.component').then(
                        m => m.AccountAddressDetailComponent,
                    ),
            },
            {
                path: 'details',
                canActivate: [AccountGuard],
                loadComponent: () =>
                    import('./account/components/account-customer-details/account-customer-details.component').then(
                        m => m.AccountCustomerDetailsComponent,
                    ),
            },
            {
                path: 'wishlist',
                canActivate: [AccountGuard],
                loadComponent: () =>
                    import('./account/components/account-wishlist/account-wishlist.component').then(
                        m => m.AccountWishlistComponent,
                    ),
            },
            {
                path: 'change-credentials',
                canActivate: [AccountGuard],
                loadComponent: () =>
                    import('./account/components/account-change-credentials/account-change-credentials.component').then(
                        m => m.AccountChangeCredentialsComponent,
                    ),
            },
            {
                path: 'sign-in',
                canActivate: [SignInGuard],
                loadComponent: () =>
                    import('./account/components/account-sign-in/account-sign-in.component').then(
                        m => m.AccountSignInComponent,
                    ),
            },
            {
                path: 'register',
                canActivate: [SignInGuard],
                loadComponent: () =>
                    import('./account/components/register/register.component').then(
                        m => m.RegisterComponent,
                    ),
            },
            {
                path: 'verify',
                canActivate: [SignInGuard],
                loadComponent: () =>
                    import('./account/components/verify/verify.component').then(
                        m => m.VerifyComponent,
                    ),
            },
            {
                path: 'reset-password',
                canActivate: [SignInGuard],
                loadComponent: () =>
                    import('./account/components/reset-password/reset-password.component').then(
                        m => m.ResetPasswordComponent,
                    ),
            },
            {
                path: 'forgotten-password',
                canActivate: [SignInGuard],
                loadComponent: () =>
                    import('./account/components/forgotten-password/forgotten-password.component').then(
                        m => m.ForgottenPasswordComponent,
                    ),
            },
            {
                path: 'change-email-address',
                canActivate: [SignInGuard],
                loadComponent: () =>
                    import('./account/components/change-email-address/change-email-address.component').then(
                        m => m.ChangeEmailAddressComponent,
                    ),
            },
        ],
    },
    {
        path: 'checkout',
        loadComponent: () =>
            import('./checkout/components/checkout-process/checkout-process.component').then(
                m => m.CheckoutProcessComponent,
            ),
        resolve: {
            activeOrder: CheckoutResolver,
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadComponent: () =>
                    import('./checkout/components/checkout-shipping/checkout-shipping.component').then(
                        m => m.CheckoutShippingComponent,
                    ),
            },
            {
                path: 'payment',
                loadComponent: () =>
                    import('./checkout/components/checkout-payment/checkout-payment.component').then(
                        m => m.CheckoutPaymentComponent,
                    ),
                resolve: {
                    activeOrder: CheckoutResolver,
                },
            },
            {
                path: 'confirmation/:code',
                canActivate: [CheckoutGuard],
                loadComponent: () =>
                    import('./checkout/components/checkout-confirmation/checkout-confirmation.component').then(
                        m => m.CheckoutConfirmationComponent,
                    ),
            },
        ],
    },
    {
        path: 'cart',
        loadComponent: () =>
            import('./core/components/cart-page/cart-page.component').then(
                m => m.CartPageComponent,
            ),
    },
    {
        path: ContactsInfo.Terms,
        loadComponent: () =>
            import('./custom/contacts-info/contacts-info.component').then(
                m => m.ContactsInfoComponent,
            ),
    },
    {
        path: ContactsInfo.Privacy,
        loadComponent: () =>
            import('./custom/contacts-info/contacts-info.component').then(
                m => m.ContactsInfoComponent,
            ),
    },
    {
        path: ContactsInfo.Faq,
        loadComponent: () =>
            import('./custom/contacts-info/contacts-info.component').then(
                m => m.ContactsInfoComponent,
            ),
    },
    {
        path: ContactsInfo.Company,
        loadComponent: () =>
            import('./custom/contacts-info/contacts-info.component').then(
                m => m.ContactsInfoComponent,
            ),
    },
    {
        path: ContactsInfo.Transport,
        loadComponent: () =>
            import('./custom/contacts-info/contacts-info.component').then(
                m => m.ContactsInfoComponent,
            ),
    },
    {
        path: ContactsInfo.Payment,
        loadComponent: () =>
            import('./custom/contacts-info/contacts-info.component').then(
                m => m.ContactsInfoComponent,
            ),
    },
    {
        path: ContactsInfo.Cookie,
        loadComponent: () =>
            import('./custom/contacts-info/contacts-info.component').then(
                m => m.ContactsInfoComponent,
            ),
    },
    {
        path: ContactsInfo.Impressum,
        loadComponent: () =>
            import('./custom/contacts-info/contacts-info.component').then(
                m => m.ContactsInfoComponent,
            ),
    },
    {
        path: ContactsInfo.Datenschutzerklaerung,
        loadComponent: () =>
            import('./custom/contacts-info/contacts-info.component').then(
                m => m.ContactsInfoComponent,
            ),
    },
    {
        path: '**',
        redirectTo: '',
    },
];
